import { ReactElement } from 'react';

/**
 * something that should never happen
 */
export const nope = (_: never) => {
  throw new Error('This should be impossible');
};

/**
 * a type that removes a specific key from itself.
 * useful for picking apart props for React props wrappers.
 *
 * e.g.
 * Omit<'foo', {foo: string; bar: number}> // {bar:number}
 */
export type Omit<Key extends keyof Type, Type> = Pick<
  Type,
  Exclude<keyof Type, Key>
>;

/**
 * we want to ban null
 */
export function deNull<T>(t: T | null | undefined): T | undefined {
  if (t === null) {
    return undefined;
  }

  return t;
}

/**
 * remove nullish elements and type it as such
 */
export function noNulls<T>(ts: Array<T | null | undefined>): T[] {
  return ts.filter((it) => it !== null && it !== undefined) as T[];
}

/**
 * return first result of a fn in a list.
 *
 * first(it => it.foo, [{foo: undefined}, {foo: 1}]) // 1
 */
export function first<T, U>(
  transform: (t: T) => U | undefined,
  items: T[]
): U | undefined {
  for (const item of items) {
    const result = transform(item);

    if (result !== undefined) {
      return result;
    }
  }

  return undefined;
}

export function identity<T>(t: T): T {
  return t;
}

/**
 * test whether something that could be null-ish is of a non-nullish type. note
 * that this _isn't_ an arbitrary boolean; it actually tells the compiler it's
 * dealing with a non-null value.
 *
 * ```javascript
 * const handleAThing = (thing: number|null) => defined(thing)
 *  ? thing.toString()
 *  : null; // yay no type error!
 * ```
 *
 * Q: I can just check this myself! A: Absolutely. However, we forbid `==`, so
 * you have to `===` check both null and undefined, which we think is quite
 * unwieldy.
 */
export function defined<T>(it: T | null | undefined): it is T {
  return it !== null && it !== undefined;
}

export const CSE_SUPPORT_ROLE = 'JASK Support';

/**
 * Check if the role is Sumo Logic CSE support role
 */
export function isCseSupport(role?: string) {
  return role === CSE_SUPPORT_ROLE;
}

export function getRoleLabel(role: string) {
  return role === CSE_SUPPORT_ROLE ? `Sumo Logic Cloud SIEM Support` : role;
}

/**
 * Replace any space in a string with a dash.
 */
export const replaceSpaces = (str: string): string =>
  str ? str.replace(/\s/g, '-') : '';

/**
 * Normalize a given key for Cypress tests.
 */
export const getNormalizedCypressKey = (key: any): string => {
  const keyType = key ? typeof key : 'none';
  let newKey: string | any[] = '';

  if (keyType === 'string') {
    newKey = replaceSpaces(key);
  } else if (keyType === 'object') {
    if (Array.isArray(key)) {
      newKey = key.map(getNormalizedCypressKey).join('');
    } else {
      const children = (key as ReactElement).props.children;
      newKey = getNormalizedCypressKey(children);
    }
  }

  return newKey;
};

/**
 * Converts the active directory custom date to javascript date object.
 * @param n - active directory time
 * @returns - javascript date object.
 */
export const ldapToJS = (n: number) => {
  // Shorter, more efficient. Uses time value for 1601-01-01 UTC
  return n > 1e17 ? new Date(n / 1e4 - 1.16444736e13) : new Date('invalid');
};

export const randomNumber = (min, max) =>
  Math.round(Math.random() * (max - min) + min);
